import * as React from 'react';
import {useApiQuery} from 'src/hooks/useApiQuery';
import type {CatalogWidget, CatalogGroup} from 'src/types/byod';
import {BYOD_API_BASE as BASE} from 'src/components/analytics-genesis/byod/api';
import {byodQueryDefaults} from 'src/components/analytics-genesis/byod/api/error-handling';


type WidgetListItem = {
  id: string;
  widget_key: string;
  widget_type: 'kpi' | 'chart' | 'table';
  name: string;
  description?: string;
  product_area: string | null;
};

type WidgetListResponse = {widgets: WidgetListItem[]};

type CatalogResult = {groups: CatalogGroup[]};

export function useWidgetCatalog(): {
  data: CatalogResult | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const {
    data: widgetData,
    isLoading,
    isError,
    refetch,
  } = useApiQuery<WidgetListResponse>(
    {pathname: `${BASE}/widgets`},
    {
      queryOptions: {
        ...byodQueryDefaults,
        queryKey: ['byod-widgets'],
        staleTime: 30 * 1000,
        gcTime: 60 * 1000,
      },
    },
  );

  const data = React.useMemo((): CatalogResult | null => {
    if (!widgetData) {
      return null;
    }

    // Group widgets by product_area
    const groupMap = new Map<string, CatalogWidget[]>();
    for (const w of widgetData.widgets ?? []) {
      const groupLabel = w.product_area ?? 'Other';
      if (!groupMap.has(groupLabel)) {
        groupMap.set(groupLabel, []);
      }
      groupMap.get(groupLabel)!.push({
        widget_key: w.widget_key,
        widget_type: w.widget_type,
        name: w.name,
        description: w.description,
        product_area: w.product_area,
      });
    }

    const groups: CatalogGroup[] = [];
    groupMap.forEach((widgets, label) => groups.push({label, widgets}));

    return {groups};
  }, [widgetData]);

  return {data, isLoading, isError, refetch: () => void refetch()};
}
